import { FormControl, FormHelperText, TextField } from "@mui/material";
import clsx from "clsx";
import { isNil } from "lodash";
import { ChangeEvent, FC, KeyboardEvent, useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { useDynamicFormsTranslate } from "@ryde/redux";
import {
  checkCleanupNestedValidation,
  DECIMAL_RADIX,
  getRegisterAttributes,
  ICustomField,
  isNonIntegerNumberKey,
  Log,
  truncateDecimals,
  ValidationHelpers,
  valueAsNumber,
} from "@ryde/shared";

import { FieldExplanationIcon } from "./_parts";
import { useInfluencedInput } from "./hooks/use-influenced-input";
import { NumberTemplateStyled } from "./number.styled";

interface INumberTemplateProps extends ICustomField {
  className?: string;
  placeholder?: string;
  /** Id of the element describing this input, so screen readers announce it (WCAG 2.1 AA). */
  ariaDescribedBy?: string;
}

export const NumberTemplate: FC<INumberTemplateProps> = ({
  id,
  name,
  size,
  formLabel,
  labelTranslated,
  formAttributes,
  formValidation,
  e2eData = "number-template",
  className,
  placeholder,
  ariaDescribedBy,
}) => {
  const { t } = useTranslation();
  const {
    control,
    setError,
    formState: { errors },
  } = useFormContext();
  const { formLabelTranslated } = useDynamicFormsTranslate({
    name,
    translatedText: labelTranslated,
    fallbackText: formLabel,
  });
  const { disabled } = getRegisterAttributes(formAttributes);
  const { customWidth, tooltipTranslationKey, helpTopikUrl } =
    formAttributes || {};

  useInfluencedInput({ formAttributes, name });

  const requiredComputed = useMemo(() => {
    return ValidationHelpers.hasRequiredRule(formValidation);
  }, [formValidation]);

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    if (formAttributes?.intRounding && isNonIntegerNumberKey(event.key)) {
      event.preventDefault();
    }
  };

  return (
    <NumberTemplateStyled
      $hasRightIcon={!!tooltipTranslationKey || !!helpTopikUrl}
    >
      <Controller
        name={name}
        control={control}
        render={({
          field: { value, onChange, ...restFieldProps },
          fieldState: { error },
        }) => {
          const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
            try {
              checkCleanupNestedValidation(name, errors);

              if (formAttributes?.intRounding && event.target.value) {
                event.target.value = parseInt(
                  event.target.value,
                  DECIMAL_RADIX,
                ).toString();
              }

              const newFieldValue = valueAsNumber(event.target.value);
              const nullOrNumber = newFieldValue === "" ? null : newFieldValue;
              onChange(nullOrNumber);
            } catch (changeError) {
              Log.debug("error setting value for numeric field ", changeError);
              setError(name, { message: t("errors.invalidNumber") });
            }
          };
          return (
            <FormControl
              required={requiredComputed}
              data-e2e-template="number-template"
              data-e2e-field-name={name}
              error={!!error}
              className={clsx("number-template", className)}
              fullWidth={!customWidth}
            >
              <TextField
                {...restFieldProps}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                label={formLabelTranslated}
                data-e2e={e2eData}
                data-e2e-id={`${name}_${id || ""}`}
                required={requiredComputed}
                disabled={disabled}
                error={!!error}
                value={
                  isNil(value) || value === "" ? "" : truncateDecimals(value)
                }
                size={size}
                fullWidth
                type="number"
                placeholder={placeholder}
                aria-describedby={ariaDescribedBy}
              />

              {error && (
                <FormHelperText data-e2e="number-template-input">
                  {error.message}
                </FormHelperText>
              )}
            </FormControl>
          );
        }}
      />
      <FieldExplanationIcon formAttributes={formAttributes} />
    </NumberTemplateStyled>
  );
};
